import { LitElement, html } from 'lit';
import { networks } from '../../../consts';

// eslint-disable-next-line
import styles from '!!lit-css-loader!postcss-loader!sass-loader!./MediaViewer.scss';

class MediaViewer extends LitElement {
  static styles = [styles];

  static get properties() {
    return {
      media: { type: Array },
      service: { type: String },
      muted: { type: Boolean },
      controls: { type: Boolean },
      loop: { type: Boolean },
      playing: { type: Boolean },
      onVideoError: { type: Function },
      showFallbackImage: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.showFallbackImage = false;
  }

  /**
   * Handles video errors and provides fallback to image
   * @param {Object} errorEvent - The error event object
   */
  handleVideoError = (errorEvent) => {
    // Critical point: Call the parent error handler if provided
    if (this.onVideoError && typeof this.onVideoError === 'function') {
      this.onVideoError(errorEvent);
    }

    // Critical point: Fallback to showing image when video fails (CORS, etc.)
    this.showFallbackImage = true;
    this.requestUpdate();
  };

  /**
   * Checks if a URL is a YouTube video
   * @param {string} url - The video URL to check
   * @returns {boolean} True if the URL is a YouTube video
   */
  static isYouTubeVideo(url) {
    if (!url) return false;
    return /(?:youtube\.com|youtu\.be)/i.test(url);
  }

  /**
   * Renders fallback image when video fails
   * @returns {TemplateResult} The fallback image template
   */
  renderFallbackImage() {
    const { large } = this.media;
    return html`
      <div class="media-viewer__fallback">
        <img class="post-image" src=${large} alt="Content preview" loading="lazy" />
        <div class="media-viewer__video-unavailable">Video unavailable</div>
      </div>
    `;
  }

  render() {
    const { large, video } = this.media;
    const { muted, controls = 0, loop, playing } = this;

    // If we've encountered an error, show the fallback image
    if (this.showFallbackImage) {
      return this.renderFallbackImage();
    }

    // YouTube videos - check both service type and URL pattern (for RSS feeds)
    if (video && (this.service === networks.YOUTUBE || MediaViewer.isYouTubeVideo(video))) {
      return html`<youtube-video
        class="media-viewer__video"
        src=${video}
        ?muted=${muted}
        playing=${playing}
        .onVideoError=${this.handleVideoError}
      ></youtube-video>`;
    }

    // Twitter videos - may have CORS issues
    if (video && this.service === networks.TWITTER) {
      return html`<twitter-video
        class="media-viewer__video"
        src=${video}
        ?muted=${muted}
        ?playing=${playing}
        .onVideoError=${this.handleVideoError}
      ></twitter-video>`;
    }

    // Instagram and other videos - often have CORS issues
    if (video) {
      return html`<instagram-video
        class="media-viewer__video"
        src=${video}
        ?loop=${loop}
        ?controls=${controls}
        ?muted=${muted}
        ?playing=${playing}
        .onVideoError=${this.handleVideoError}
      ></instagram-video>`;
    }

    // No video, just show the image
    return html`<img class="post-image" src=${large} alt="Content preview" loading="lazy" />`;
  }
}

export default MediaViewer;
